import type { IMappedGeoMappingItem } from 'partner360/components/filters'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IEnrichedCockpitFinSobRow } from '../../model/types'
import {
  EMPTY_EXPECTED_EXIT,
  getRevenueAndSobActuals,
  getRevenueAndSobBaseline,
  getRevenueAndSobExpectedExit,
  getRevenueAndSobMarketGrowth,
  getRevenueAndSobPlan,
  getRevenueAndSobQuota,
  IRevenueAndSobExpectedExit,
  LAST_FQUARTER,
} from '../revenueAndSob'

import {
  TOTAL_ROW_ID,
  UNKNOWN_OFFERING_ORDER,
  UNMAPPED_GEO_KEY,
  UNMAPPED_GEO_LABEL,
  UNMAPPED_GEO_ORDER,
} from './constants'
import { buildGeoMappingTree, IGeoMappingTree } from './geoMappingHierarchy'
import {
  getLevelValue,
  getProductPath,
  isNodeDefiningRow,
  isOverallLevelValue,
  PRODUCT_LEVELS,
} from './levelHelpers'
import { normalizeGeoKey } from './normalizeGeoKey'
import {
  IRevenueAndSobHierarchyFigures,
  IRevenueAndSobHierarchyRow,
  TRevenueAndSobHierarchyOrder,
  TRevenueAndSobProductLevel,
} from './types'

/** `geoDepth` on a row that isn't a Geo node — see `IRevenueAndSobHierarchyRow.geoDepth`. */
const NO_GEO_DEPTH = -1

/** `partnerDepth` on a row that isn't a Partner leaf — see
 *  `IRevenueAndSobHierarchyRow.partnerDepth`. */
const NO_PARTNER_DEPTH = -1

export interface IGetRevenueAndSobHierarchyRowsParams {
  targetRows: IEnrichedCockpitFinSobRow[]
  actualsRows: IEnrichedCockpitFinSobRow[]
  quotaRows: IEnrichedCockpitFinSobRow[]
  /** Whether the Geo axis is enabled at all — manager cockpit mode only. When `false`, every other
   *  geo-related param is ignored and the result is the Product tree alone, roots at `null`. */
  showGeoHierarchy: boolean
  geoMappingItems: IMappedGeoMappingItem[]
  /** Which axis is outer when `showGeoHierarchy` is `true`. Ignored otherwise. */
  hierarchyOrder: TRevenueAndSobHierarchyOrder
  selectedFYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  isYtd: boolean
  currentFQuarter: number
  fQuarterActualsPercentage: number
  /** Whether the selected period/quarter is still open (computed once via `isPeriodOpen`).
   *  Expected exit and actuals never both show — whichever this excludes comes back `null`. */
  isPeriodOpen: boolean
  /** Whether the year before `selectedFYear` is still in its last quarter
   *  (`selectIsCockpitPrevFYearClosing`). The table then shows that year's expected exit in place
   *  of this year's own, so the one it leaves out comes back `null`. */
  isPrevFYearClosing: boolean
}

interface IScopedFinSobRows {
  target: IEnrichedCockpitFinSobRow[]
  actuals: IEnrichedCockpitFinSobRow[]
  quota: IEnrichedCockpitFinSobRow[]
}

type IFigureContext = Omit<
  IGetRevenueAndSobHierarchyRowsParams,
  | 'targetRows'
  | 'actualsRows'
  | 'quotaRows'
  | 'showGeoHierarchy'
  | 'geoMappingItems'
  | 'hierarchyOrder'
>

const allScopedRows = (scoped: IScopedFinSobRows): IEnrichedCockpitFinSobRow[] => [
  ...scoped.target,
  ...scoped.actuals,
  ...scoped.quota,
]

const filterScoped = (
  scoped: IScopedFinSobRows,
  predicate: (row: IEnrichedCockpitFinSobRow) => boolean
): IScopedFinSobRows => ({
  target: scoped.target.filter(predicate),
  actuals: scoped.actuals.filter(predicate),
  quota: scoped.quota.filter(predicate),
})

/** `rows`, scoped down to whichever ones represent this node's own figures without double-counting
 *  its subtree: the backend's own pre-aggregated total row(s) for this exact product depth when it
 *  sent one, otherwise every row in scope summed together instead — e.g. when a subCategory was
 *  never given its own `l3Category: 'jbp-overall'` row, its total is built by summing every one of
 *  its l3Categories. Mirrors the deleted `revenueAndSobHierarchy/getProductNodes.ts`'s
 *  `getRowsForProductNode`, generalized to run row-wise (via `isNodeDefiningRow`) instead of against
 *  one pre-resolved node, so the same call works whether or not a product level has been fixed yet.
 *
 *  The own-row-or-fallback decision is made independently per distinct path up to
 *  `productLevelIndex` (not once across the whole `rows` array) — critical when `productLevelIndex`
 *  is `0` and `rows` still spans multiple offerings (a pure Geo node, or the whole table when Geo
 *  is disabled): one offering having its own total row must never suppress another offering's
 *  fallback subtree sum. */
const selectOwnProductRows = (
  rows: IEnrichedCockpitFinSobRow[],
  productLevelIndex: number
): IEnrichedCockpitFinSobRow[] => {
  const rowsByPath = new Map<string, IEnrichedCockpitFinSobRow[]>()

  rows.forEach((row) => {
    const key = getProductPath(row, productLevelIndex).join('::')
    const group = rowsByPath.get(key)

    if (group) {
      group.push(row)
    } else {
      rowsByPath.set(key, [row])
    }
  })

  return [...rowsByPath.values()].flatMap((group) => {
    const ownRows = group.filter((row) => isNodeDefiningRow(row, productLevelIndex))
    return ownRows.length > 0 ? ownRows : group
  })
}

const toOwnScoped = (scoped: IScopedFinSobRows, productLevelIndex: number): IScopedFinSobRows => ({
  target: selectOwnProductRows(scoped.target, productLevelIndex),
  actuals: selectOwnProductRows(scoped.actuals, productLevelIndex),
  quota: selectOwnProductRows(scoped.quota, productLevelIndex),
})

/** The settings an expected exit reads — the part of `IFigureContext` that
 *  `getRevenueAndSobHierarchyExpectedExit` needs from its callers. */
type IExpectedExitContext = Pick<
  IFigureContext,
  | 'selectedFYear'
  | 'currentFQuarter'
  | 'actualsType'
  | 'isLocalCurrency'
  | 'isPeriodOpen'
  | 'isPrevFYearClosing'
>

/** One node's expected exit, with the two halves it adds up to — or nothing at all for a period
 *  with nothing left open, where the table shows actuals instead, or while the previous year is
 *  closing, where it shows that year's instead. Shared by `buildFigures` and
 *  `getRevenueAndSobHierarchyExpectedExit`, so the whole table's figure is the Total row's by
 *  construction. */
const getNodeExpectedExit = (
  scoped: IScopedFinSobRows,
  ctx: IExpectedExitContext
): IRevenueAndSobExpectedExit => {
  const {
    selectedFYear,
    currentFQuarter,
    actualsType,
    isLocalCurrency,
    isPeriodOpen,
    isPrevFYearClosing,
  } = ctx

  if (!isPeriodOpen || isPrevFYearClosing) {
    return EMPTY_EXPECTED_EXIT
  }

  return getRevenueAndSobExpectedExit({
    rows: [...scoped.actuals, ...scoped.target],
    selectedFYear,
    currentFQuarter,
    actualsType,
    isLocalCurrency,
    categoryRowScope: 'subtree',
  })
}

/** While the year before the selected one is still in its last quarter: where that year is
 *  expected to exit — Q1–Q3 as booked, Q4 as planned. Nothing at all otherwise. */
const getNodePrevFYearExpectedExit = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext
): IRevenueAndSobExpectedExit => {
  const { selectedFYear, actualsType, isLocalCurrency, isPrevFYearClosing } = ctx

  if (!isPrevFYearClosing) {
    return EMPTY_EXPECTED_EXIT
  }

  return getRevenueAndSobExpectedExit({
    rows: [...scoped.actuals, ...scoped.target],
    selectedFYear: selectedFYear - 1,
    currentFQuarter: LAST_FQUARTER,
    actualsType,
    isLocalCurrency,
    categoryRowScope: 'subtree',
  })
}

/** One node's Sales/SOB figures, computed the same way
 *  `../revenueAndSob/getRevenueAndSobByCategory.ts`'s `buildRow` computes a category row's —
 *  `categoryRowScope: 'subtree'` takes `scoped`'s rows as-is (no further category filtering),
 *  since `scoped` has already been narrowed to exactly this node's own contributing rows by
 *  `toOwnScoped`. */
const buildFigures = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext
): { sales: IRevenueAndSobHierarchyFigures; sob: IRevenueAndSobHierarchyFigures } => {
  const {
    selectedFYear,
    actualsType,
    isLocalCurrency,
    isYtd,
    currentFQuarter,
    fQuarterActualsPercentage,
    isPeriodOpen,
  } = ctx

  const baseline = getRevenueAndSobBaseline({
    rows: scoped.actuals,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    currentFQuarter,
    fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
    categoryRowScope: 'subtree',
  })

  const plan = getRevenueAndSobPlan({
    rows: scoped.target,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    currentFQuarter,
    fQuarterActualsPercentage: isYtd ? fQuarterActualsPercentage : null,
    categoryRowScope: 'subtree',
  })

  const expectedExit = getNodeExpectedExit(scoped, ctx)
  const prevFYearExpectedExit = getNodePrevFYearExpectedExit(scoped, ctx)

  const actuals = isPeriodOpen
    ? { sales: null, sob: null }
    : getRevenueAndSobActuals({
        rows: scoped.actuals,
        selectedFYear,
        actualsType,
        isLocalCurrency,
        categoryRowScope: 'subtree',
      })

  const quota = getRevenueAndSobQuota({
    rows: scoped.quota,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    categoryRowScope: 'subtree',
  })

  const marketGrowth = getRevenueAndSobMarketGrowth({
    rows: [...scoped.actuals, ...scoped.target],
    selectedFYear,
    categoryRowScope: 'subtree',
  })

  return {
    sales: {
      baseline: baseline.sales,
      plan: plan.sales,
      expectedExit: expectedExit.sales,
      prevFYearExpectedExit: prevFYearExpectedExit.sales,
      actuals: actuals.sales,
      quota: quota.sales,
      marketGrowth: marketGrowth.sales,
    },
    sob: {
      baseline: baseline.sob,
      plan: plan.sob,
      expectedExit: expectedExit.sob,
      prevFYearExpectedExit: prevFYearExpectedExit.sob,
      actuals: actuals.sob,
      quota: quota.sob,
      marketGrowth: marketGrowth.sob,
    },
  }
}

/** How `row` participates in the Geo axis at `geoDepth`:
 *  - `node` — its `geoMappingItems` node at that depth;
 *  - `'unmapped'` — its `countryCode` matched no node at all, which only surfaces at depth `0`;
 *  - `undefined` — its chain is simply shorter than `geoDepth`, so its branch has already ended
 *    (*not* the same as unmapped: a 2-deep branch must not sprout Unmapped children at depth 2).
 *
 *  That last case also covers a country listed on a node but on none of its children — `CA` on
 *  `North America` with no Canada node. Such a row counts toward `North America`'s own figures (a
 *  node's figures come from its own rows, never from summing its children) but appears under no
 *  child of it, so that node's children can legitimately sum to less than the node itself. Same
 *  semantics as the `GeoMapping` filter, which has no node to offer for that country either. */
const getGeoPlacement = (
  row: IEnrichedCockpitFinSobRow,
  geoDepth: number,
  geoTree: IGeoMappingTree
): IMappedGeoMappingItem | 'unmapped' | undefined => {
  const chain = geoTree.chainByCountry.get(normalizeGeoKey(row.countryCode))

  if (!chain?.length) {
    return geoDepth === 0 ? 'unmapped' : undefined
  }

  return chain[geoDepth]
}

interface IGeoBucket {
  key: string
  label: string
  position: number
  scoped: IScopedFinSobRows
}

/** The distinct Geo nodes present in `scoped` at `geoDepth`, sorted by each node's position in
 *  `geoMappingItems` — the order the `GeoMapping` filter renders its siblings in, since
 *  `TreeSelect` nests that flat array in array order (see `IGeoMappingTree.positionById` for why
 *  the node's own `order` field can't do this job). Unmapped has no node at all, so it sorts last
 *  via `UNMAPPED_GEO_ORDER`, the same convention `getProductBuckets` uses for
 *  `UNKNOWN_OFFERING_ORDER`. Empty once no row in scope reaches this depth, which is what
 *  terminates the Geo recursion — the tree's own shape decides how many levels there are, and
 *  branches may end at different depths. Bucket membership is discovered across all three row
 *  arrays combined (a bucket with quota-only rows still needs to appear), but each bucket carries
 *  its own independently filtered target/actuals/quota subsets. */
const getGeoBuckets = (
  scoped: IScopedFinSobRows,
  geoDepth: number,
  geoTree: IGeoMappingTree
): IGeoBucket[] => {
  const buckets = new Map<string, IGeoBucket>()

  /** One pass per row array, pushing each row into its bucket — rather than discovering the keys
   *  first and then re-scanning all three arrays once per bucket. Same result (bucket membership
   *  was always discovered across the three arrays combined, with each bucket keeping its own
   *  target/actuals/quota subsets), but O(rows) per depth instead of O(rows × buckets), which
   *  matters now that the axis recurses to the tree's real depth. */
  const collect = (rows: IEnrichedCockpitFinSobRow[], kind: keyof IScopedFinSobRows) => {
    rows.forEach((row) => {
      const placement = getGeoPlacement(row, geoDepth, geoTree)

      if (placement === undefined) {
        return
      }

      const isUnmapped = placement === 'unmapped'
      const key = isUnmapped ? UNMAPPED_GEO_KEY : placement.itemId
      let bucket = buckets.get(key)

      if (!bucket) {
        bucket = {
          key,
          label: isUnmapped ? UNMAPPED_GEO_LABEL : placement.name,
          position: isUnmapped ? UNMAPPED_GEO_ORDER : (geoTree.positionById.get(key) ?? 0),
          scoped: { target: [], actuals: [], quota: [] },
        }
        buckets.set(key, bucket)
      }

      bucket.scoped[kind].push(row)
    })
  }

  collect(scoped.target, 'target')
  collect(scoped.actuals, 'actuals')
  collect(scoped.quota, 'quota')

  return [...buckets.values()].sort((left, right) => left.position - right.position)
}

const getProductOrder = (
  row: IEnrichedCockpitFinSobRow,
  level: TRevenueAndSobProductLevel
): number => {
  switch (level) {
    case 'offering':
      return row.offeringOrder
    case 'category':
      return row.categoryOrder
    case 'subCategory':
      return row.subCategoryOrder
    case 'l3Category':
      return UNKNOWN_OFFERING_ORDER
  }
}

interface IProductBucket {
  key: string
  order: number
  scoped: IScopedFinSobRows
}

/** The distinct Product nodes present in `scoped` at `level`, sorted by the level's configured
 *  order (offering/category/subCategory tab-strip order; l3Category has no order source of its
 *  own, so it sorts last). A row whose own value at `level` is the `OVERALL_CATEGORY` sentinel (or
 *  missing) is that level's parent total, not a node here — see `isOverallLevelValue`. */
const getProductBuckets = (
  scoped: IScopedFinSobRows,
  level: TRevenueAndSobProductLevel
): IProductBucket[] => {
  const orderByKey = new Map<string, number>()

  allScopedRows(scoped).forEach((row) => {
    const value = getLevelValue(row, level)

    if (level !== 'offering' && isOverallLevelValue(value)) {
      return
    }

    if (!orderByKey.has(value)) {
      orderByKey.set(value, getProductOrder(row, level))
    }
  })

  return [...orderByKey.entries()]
    .map(([key, order]) => ({
      key,
      order,
      scoped: filterScoped(scoped, (row) => getLevelValue(row, level) === key),
    }))
    .sort((left, right) => left.order - right.order || left.key.localeCompare(right.key))
}

const rowId = (parentRowId: string | null, level: string, key: string) =>
  parentRowId === null
    ? `${level}::${encodeURIComponent(key)}`
    : `${parentRowId}::${level}::${encodeURIComponent(key)}`

interface IPartnerBucket {
  key: string
  scoped: IScopedFinSobRows
}

/** The distinct partners present in `scoped`, collected the same way `getGeoBuckets` collects geo
 *  nodes — one pass per row array, so membership is discovered across target/actuals/quota combined
 *  (a partner with quota-only rows still needs a row) while each bucket keeps its own three
 *  subsets.
 *
 *  Deliberately **unsorted**: the buckets come back in the order the rows first mention them, which
 *  follows the payload's own partner grouping. Unlike the Geo and Product axes there is no
 *  configured order to honour here, and the requested behaviour is to leave the order alone — so
 *  this is not a missing `.sort()`. */
const getPartnerBuckets = (scoped: IScopedFinSobRows): IPartnerBucket[] => {
  const buckets = new Map<string, IPartnerBucket>()

  const collect = (rows: IEnrichedCockpitFinSobRow[], kind: keyof IScopedFinSobRows) => {
    rows.forEach((row) => {
      let bucket = buckets.get(row.partnerId)

      if (!bucket) {
        bucket = { key: row.partnerId, scoped: { target: [], actuals: [], quota: [] } }
        buckets.set(row.partnerId, bucket)
      }

      bucket.scoped[kind].push(row)
    })
  }

  collect(scoped.target, 'target')
  collect(scoped.actuals, 'actuals')
  collect(scoped.quota, 'quota')

  return [...buckets.values()]
}

/** Every Product level (`productLevelIndex..l3Category`) nested under `parentRowId`, flattened into
 *  a single array, optionally inheriting a Geo scope it states but does not itself break down.
 *  Mirrors `getProductRows` inside the Key Initiatives tile's `getCockpitInitiativeHierarchyRows.ts`,
 *  generalized from 2 fixed levels to the `PRODUCT_LEVELS`-driven depth this table needs.
 *
 *  The Product axis grows no Partner leaves of its own — partners hang off the Geo axis only, so
 *  that a partner's name appears in one column rather than two. This is every caller's product
 *  walker: the partner-cockpit path (Geo axis disabled), the subtree under each Geo node, the
 *  `geoProduct` secondary tree, and the breakdown under a Geo-axis Partner leaf.
 *
 *  Attached under *every* Geo node rather than only the deepest, so a user can expand a Region
 *  straight into its own product breakdown without first drilling into a Market. */
const buildProductTree = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext,
  productLevelIndex: number,
  parentRowId: string | null,
  geoKey: string,
  geoLabel: string
): IRevenueAndSobHierarchyRow[] => {
  if (productLevelIndex >= PRODUCT_LEVELS.length) {
    return []
  }

  const level = PRODUCT_LEVELS[productLevelIndex]

  return getProductBuckets(scoped, level).flatMap((bucket) => {
    const id = rowId(parentRowId, level, bucket.key)
    const node: IRevenueAndSobHierarchyRow = {
      rowId: id,
      parentRowId,
      level,
      geoKey,
      geoLabel,
      geoDepth: NO_GEO_DEPTH,
      productKey: bucket.key,
      partnerId: '',
      partnerDepth: NO_PARTNER_DEPTH,
      ...buildFigures(toOwnScoped(bucket.scoped, productLevelIndex), ctx),
    }
    const children = buildProductTree(
      bucket.scoped,
      ctx,
      productLevelIndex + 1,
      id,
      geoKey,
      geoLabel
    )

    return [node, ...children]
  })
}

/** One leaf per partner behind `scoped`, hung under the deepest node of the **Geo** axis. Manager
 *  cockpit mode only.
 *
 *  Partners hang off the Geo axis alone, so they render in the Geo column and take the Geo column's
 *  expand set. That caret is always free: a node only reaches here when its own axis has no further
 *  children, so nothing else is competing for it — the other axis's toggle on the same row (the
 *  "Offering" root) is untouched.
 *
 *  The leaf inherits its parent's `geoKey`/`geoLabel`/`productKey` so it still states which cell it
 *  belongs to, and computes its figures through the same `toOwnScoped` + `buildFigures` pair every
 *  other node uses at this `productLevelIndex`. That is what keeps a partner's SOB a
 *  revenue-weighted average and leaves its parent's own figures untouched — a node's figures come
 *  from its own rows, never from summing its children, so appending this level cannot move any
 *  number already on screen.
 *
 *  A leaf carrying no product scope of its own additionally opens into the full Product tree, so the
 *  bottom of a geo drill can be broken down by product the same way every Geo node above it already
 *  can. That subtree is the other axis, in the other column, under its own caret — free for exactly
 *  the reason above. */
const buildPartnerLeaves = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext,
  parentRowId: string,
  geoKey: string,
  geoLabel: string,
  productKey: string,
  productLevelIndex: number,
  partnerDepth: number
): IRevenueAndSobHierarchyRow[] =>
  getPartnerBuckets(scoped).flatMap((bucket): IRevenueAndSobHierarchyRow[] => {
    const id = rowId(parentRowId, 'partner', bucket.key)
    const node: IRevenueAndSobHierarchyRow = {
      rowId: id,
      parentRowId,
      level: 'partner',
      geoKey,
      geoLabel,
      geoDepth: NO_GEO_DEPTH,
      productKey,
      partnerId: bucket.key,
      partnerDepth,
      ...buildFigures(toOwnScoped(bucket.scoped, productLevelIndex), ctx),
    }

    /** A leaf already scoped to a product (Product > Geo order, under an Offering) would have to
     *  start its breakdown mid-axis at Category, which the shared "Offering" root label can't
     *  describe — so it stays terminal. */
    if (productKey !== '') {
      return [node]
    }

    return [node, ...buildProductTree(bucket.scoped, ctx, productLevelIndex, id, geoKey, geoLabel)]
  })

/** Every remaining Geo depth (`geoDepth` and below) nested under one fixed Product scope, flattened
 *  into a single array — the mirror image of `buildProductTree`, attached under every Product node
 *  (offering, category, subCategory, and l3Category alike). Recursion ends when no row in scope
 *  reaches the next depth, so the `geoMappingItems` tree's own shape decides how deep it goes — and
 *  whichever depth a branch ends at is where its Partner leaves hang, indented one step past the
 *  node's own `geoDepth + 1`. */
const buildGeoSubtree = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext,
  geoTree: IGeoMappingTree,
  geoDepth: number,
  parentRowId: string,
  productKey: string,
  productLevelIndex: number
): IRevenueAndSobHierarchyRow[] =>
  getGeoBuckets(scoped, geoDepth, geoTree).flatMap((bucket) => {
    const id = rowId(parentRowId, 'geo', bucket.key)
    const node: IRevenueAndSobHierarchyRow = {
      rowId: id,
      parentRowId,
      level: 'geo',
      geoKey: bucket.key,
      geoLabel: bucket.label,
      geoDepth,
      productKey,
      partnerId: '',
      partnerDepth: NO_PARTNER_DEPTH,
      ...buildFigures(toOwnScoped(bucket.scoped, productLevelIndex), ctx),
    }
    const children = buildGeoSubtree(
      bucket.scoped,
      ctx,
      geoTree,
      geoDepth + 1,
      id,
      productKey,
      productLevelIndex
    )

    if (children.length > 0) {
      return [node, ...children]
    }

    return [
      node,
      ...buildPartnerLeaves(
        bucket.scoped,
        ctx,
        id,
        bucket.key,
        bucket.label,
        productKey,
        productLevelIndex,
        geoDepth + 2
      ),
    ]
  })

/** The primary tree for `hierarchyOrder === 'geoProduct'`: the `geoMappingItems` tree top-down,
 *  as deep as it actually goes, rooted at `TOTAL_ROW_ID` — with every Geo node additionally opening
 *  into its own Product subtree (`buildProductTree`). */
const buildGeoThenProductRows = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext,
  geoTree: IGeoMappingTree,
  geoDepth: number,
  parentRowId: string | null,
  productLevelIndex: number
): IRevenueAndSobHierarchyRow[] =>
  getGeoBuckets(scoped, geoDepth, geoTree).flatMap((bucket) => {
    const id = rowId(parentRowId, 'geo', bucket.key)
    const node: IRevenueAndSobHierarchyRow = {
      rowId: id,
      parentRowId,
      level: 'geo',
      geoKey: bucket.key,
      geoLabel: bucket.label,
      geoDepth,
      productKey: '',
      partnerId: '',
      partnerDepth: NO_PARTNER_DEPTH,
      ...buildFigures(toOwnScoped(bucket.scoped, productLevelIndex), ctx),
    }
    const productChildren = buildProductTree(
      bucket.scoped,
      ctx,
      productLevelIndex,
      id,
      bucket.key,
      bucket.label
    )
    const geoChildren = buildGeoThenProductRows(
      bucket.scoped,
      ctx,
      geoTree,
      geoDepth + 1,
      id,
      productLevelIndex
    )

    /** Gated on this node's **own** axis only. A Geo node keeps its Product subtree either way —
     *  that is the other axis, in the other column, under its own caret — so having one must not
     *  suppress the partner breakdown at the bottom of a geo drill. */
    const partnerChildren =
      geoChildren.length === 0
        ? buildPartnerLeaves(
            bucket.scoped,
            ctx,
            id,
            bucket.key,
            bucket.label,
            '',
            productLevelIndex,
            geoDepth + 2
          )
        : []

    return [node, ...productChildren, ...geoChildren, ...partnerChildren]
  })

/** The primary tree for `hierarchyOrder === 'productGeo'`: Offering → Category → SubCategory →
 *  L3Category, rooted at `TOTAL_ROW_ID` — with every Product node additionally opening into its own
 *  Geo subtree (`buildGeoSubtree`). */
const buildProductThenGeoRows = (
  scoped: IScopedFinSobRows,
  ctx: IFigureContext,
  geoTree: IGeoMappingTree,
  productLevelIndex: number,
  parentRowId: string | null,
  geoKey: string,
  geoLabel: string
): IRevenueAndSobHierarchyRow[] => {
  if (productLevelIndex >= PRODUCT_LEVELS.length) {
    return []
  }

  const level = PRODUCT_LEVELS[productLevelIndex]

  return getProductBuckets(scoped, level).flatMap((bucket) => {
    const id = rowId(parentRowId, level, bucket.key)
    const node: IRevenueAndSobHierarchyRow = {
      rowId: id,
      parentRowId,
      level,
      geoKey,
      geoLabel,
      geoDepth: NO_GEO_DEPTH,
      productKey: bucket.key,
      partnerId: '',
      partnerDepth: NO_PARTNER_DEPTH,
      ...buildFigures(toOwnScoped(bucket.scoped, productLevelIndex), ctx),
    }
    const geoChildren = buildGeoSubtree(
      bucket.scoped,
      ctx,
      geoTree,
      0,
      id,
      bucket.key,
      productLevelIndex
    )
    const productChildren = buildProductThenGeoRows(
      bucket.scoped,
      ctx,
      geoTree,
      productLevelIndex + 1,
      id,
      geoKey,
      geoLabel
    )

    return [node, ...geoChildren, ...productChildren]
  })
}

/** One row per Geo/Product node reachable from the current axis configuration, plus (manager
 *  cockpit mode only, `showGeoHierarchy: true`) a synthetic grand Total row — the data behind the
 *  expanded Revenue & SOB view's hierarchy table. Mirrors `getCockpitInitiativeHierarchyRows`'s
 *  split into a "primary" tree (the chosen outer axis, with the inner axis nested at every level)
 *  and a "secondary" tree (the inner axis's own breakdown rooted directly at Total, for when a user
 *  expands that axis before drilling into the outer one at all).
 *
 *  The last node of the **Geo** axis additionally grows one Partner leaf per partner behind it, so a
 *  partner's name appears in one column rather than two. "Last of its axis" is judged on the Geo axis
 *  alone: a Geo node with no sub-geos qualifies even though it still opens into its own Product
 *  subtree, since that subtree is the other axis, in the other column, under its own caret. The
 *  Product axis grows none of its own — drilling Product alone never reaches a partner.
 *
 *  A Partner leaf that carries no product scope of its own is in turn broken down by product — see
 *  `buildPartnerLeaves` — so the deepest level overall is an L3Category under a partner. One already
 *  scoped to a product (Product > Geo order, under an Offering) stays terminal.
 *
 *  Partner mode (`showGeoHierarchy: false`) has no Total row and no Partner leaves: the Product
 *  tree's own top-level Offering nodes get `parentRowId: null` directly — a single partner has one
 *  geo, so there's nothing for a Total row to roll up across and nothing for a partner breakdown to
 *  separate. Gating the new level on this same early return is what keeps that cockpit untouched. */
export const getRevenueAndSobHierarchyRows = ({
  targetRows,
  actualsRows,
  quotaRows,
  showGeoHierarchy,
  geoMappingItems,
  hierarchyOrder,
  ...ctx
}: IGetRevenueAndSobHierarchyRowsParams): IRevenueAndSobHierarchyRow[] => {
  const scoped: IScopedFinSobRows = { target: targetRows, actuals: actualsRows, quota: quotaRows }

  if (!showGeoHierarchy) {
    return buildProductTree(scoped, ctx, 0, null, '', '')
  }

  const geoTree = buildGeoMappingTree(geoMappingItems)

  const totalRow: IRevenueAndSobHierarchyRow = {
    rowId: TOTAL_ROW_ID,
    parentRowId: null,
    level: 'total',
    geoKey: '',
    geoLabel: '',
    geoDepth: NO_GEO_DEPTH,
    productKey: '',
    partnerId: '',
    partnerDepth: NO_PARTNER_DEPTH,
    ...buildFigures(toOwnScoped(scoped, 0), ctx),
  }

  const primaryRows =
    hierarchyOrder === 'geoProduct'
      ? buildGeoThenProductRows(scoped, ctx, geoTree, 0, TOTAL_ROW_ID, 0)
      : buildProductThenGeoRows(scoped, ctx, geoTree, 0, TOTAL_ROW_ID, '', '')

  const secondaryRows =
    hierarchyOrder === 'geoProduct'
      ? buildProductTree(scoped, ctx, 0, TOTAL_ROW_ID, '', '')
      : buildGeoSubtree(scoped, ctx, geoTree, 0, TOTAL_ROW_ID, '', 0)

  return [totalRow, ...secondaryRows, ...primaryRows]
}

interface IGetRevenueAndSobHierarchyExpectedExitParams extends IExpectedExitContext {
  targetRows: IEnrichedCockpitFinSobRow[]
  actualsRows: IEnrichedCockpitFinSobRow[]
}

/** The whole table's expected exit, with the two halves it adds up to — the figure its Total row
 *  carries in manager cockpit mode, worked out the same way in partner mode, which has no Total
 *  row. One per table rather than one per node: the Expected Exit headers' tooltips state it. */
export const getRevenueAndSobHierarchyExpectedExit = ({
  targetRows,
  actualsRows,
  ...ctx
}: IGetRevenueAndSobHierarchyExpectedExitParams): IRevenueAndSobExpectedExit =>
  getNodeExpectedExit(toOwnScoped({ target: targetRows, actuals: actualsRows, quota: [] }, 0), ctx)
